import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, EmptyState } from '@teals/ui-web';
import { useAuth } from '../../hooks/useAuth';
import { useOrgRole } from '../../hooks/useOrgRole';
import { useGetOrgModelsQuery } from '../../store/api/orgModelsApi';
import type { OrgModel } from '../../store/api/orgModelsApi';
import { labLabel } from '../../utils/modelDisplay';
import {
  benchmarkByLabel,
  canTryModels,
  costTierDots,
  costTierLabel,
  formatTokens,
  parseModelIds,
} from './modelsShared';
import {
  ActionBar,
  BackLink,
  Header,
  HeaderCopy,
  Meta,
  Muted,
  Page,
  Subtitle,
  Title,
} from './modelsStyles';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  table-layout: fixed;

  th,
  td {
    text-align: left;
    vertical-align: top;
    padding: 0.75rem;
    border-bottom: 1px solid var(--border);
  }

  thead th {
    font-size: 1rem;
    color: var(--text-primary);
  }

  tbody th {
    width: 10rem;
    font-weight: 500;
    color: var(--text-muted);
  }
`;

const Best = styled.span`
  margin-left: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--accent);
`;

const RemoveButton = styled.button`
  display: block;
  margin-top: 0.25rem;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.75rem;
  color: var(--text-muted);
  cursor: pointer;
`;

type CompareRow = {
  label: string;
  render: (m: OrgModel) => React.ReactNode;
  /** Higher is better; omit for rows without a "best". */
  rank?: (m: OrgModel) => number | null | undefined;
};

function benchmarkRow(label: string): CompareRow {
  return {
    label,
    render: (m) => {
      const b = benchmarkByLabel(m, label);
      return b ? (
        <>
          {b.score}%<Meta>{b.name} · {b.asOf}</Meta>
        </>
      ) : (
        <Meta>Not published</Meta>
      );
    },
    rank: (m) => benchmarkByLabel(m, label)?.score,
  };
}

const ROWS: CompareRow[] = [
  {
    label: 'Relative cost',
    render: (m) => (
      <>
        {costTierLabel(m.costTier)} <Meta>{costTierDots(m.costTier)}</Meta>
      </>
    ),
    rank: (m) => (m.costTier ? -m.costTier : null),
  },
  {
    label: 'Context window',
    render: (m) => formatTokens(m.contextWindow),
    rank: (m) => m.contextWindow,
  },
  {
    label: 'Max output',
    render: (m) => formatTokens(m.maxOutputTokens),
    rank: (m) => m.maxOutputTokens,
  },
  benchmarkRow('Reasoning'),
  benchmarkRow('Coding'),
  benchmarkRow('Knowledge'),
  {
    label: 'Capabilities',
    render: (m) => (m.capabilities.length ? m.capabilities.join(', ') : '—'),
  },
  { label: 'Best for', render: (m) => m.bestFor?.join('; ') || '—' },
  { label: 'Lab', render: (m) => labLabel(m.lab) || '—' },
  { label: 'Status', render: (m) => (m.enabled ? 'On' : 'Off') },
];

function bestIds(models: OrgModel[], rank?: CompareRow['rank']): Set<string> {
  if (!rank) return new Set();
  const scored = models
    .map((m) => ({ id: m.productId, value: rank(m) }))
    .filter((s): s is { id: string; value: number } => typeof s.value === 'number');
  if (scored.length < 2) return new Set();
  const top = Math.max(...scored.map((s) => s.value));
  const winners = scored.filter((s) => s.value === top);
  return winners.length === scored.length ? new Set() : new Set(winners.map((w) => w.id));
}

const ModelCompare: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { role } = useOrgRole();
  const { data: models = [], isLoading } = useGetOrgModelsQuery(orgId || '', {
    skip: !orgId,
  });
  const ids = parseModelIds(searchParams.get('ids'));
  const selected = useMemo(
    () =>
      ids
        .map((id) => models.find((m) => m.productId === id))
        .filter((m): m is OrgModel => Boolean(m)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [models, ids.join(',')],
  );

  const remove = (productId: string) => {
    const next = ids.filter((id) => id !== productId);
    setSearchParams(next.length ? { ids: next.join(',') } : {}, { replace: true });
  };

  if (!orgId) return null;

  return (
    <Page>
      <BackLink type="button" onClick={() => navigate('/models')}>
        ← Models
      </BackLink>
      <Header>
        <HeaderCopy>
          <Title>Compare models</Title>
          <Subtitle>Side by side, up to three at a time.</Subtitle>
        </HeaderCopy>
        {selected.length > 0 && canTryModels(role) && (
          <ActionBar>
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  `/models/playground?ids=${selected.map((m) => m.productId).join(',')}`,
                )
              }
            >
              Try together
            </Button>
          </ActionBar>
        )}
      </Header>

      {isLoading ? (
        <Muted>Loading models…</Muted>
      ) : selected.length < 2 ? (
        <EmptyState
          title="Pick at least two models"
          description="Select models on the Models page, then choose Compare."
          action={<Button onClick={() => navigate('/models')}>Choose models</Button>}
        />
      ) : (
        <>
          <Table aria-label="Model comparison">
            <thead>
              <tr>
                <th aria-label="Attribute" />
                {selected.map((m) => (
                  <th key={m.productId} scope="col">
                    <Link to={`/models/${encodeURIComponent(m.productId)}`}>
                      {m.displayName}
                    </Link>
                    <RemoveButton
                      type="button"
                      aria-label={`Remove ${m.displayName}`}
                      onClick={() => remove(m.productId)}
                    >
                      Remove
                    </RemoveButton>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => {
                const best = bestIds(selected, row.rank);
                return (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    {selected.map((m) => (
                      <td key={m.productId}>
                        {row.render(m)}
                        {best.has(m.productId) && <Best>Best</Best>}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Muted>
            Cost is relative, not a rate. Benchmarks are reported by each lab and
            differ by model generation; &quot;Not published&quot; means no score is
            available.
          </Muted>
        </>
      )}
    </Page>
  );
};

export default ModelCompare;
